import {
  FormControl,
  Stack,
  Table,
  TableCell,
  TableRow,
  TextField,
  Typography,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Button,
  FormHelperText,
  Box,
  CircularProgress,
} from "@mui/material";
import React, { useState, useEffect, useCallback } from "react";
import Notification from "../../Common/Notification";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import AlertToast from "../common/Toast";
import { apiConstants } from "../../constants";
import { authHeader, getUserToken } from "../../helpers";
import { tr } from "date-fns/locale";
import debounce from "lodash/debounce";

// Styled header row for tables
const StyledHeaderRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.primary.dark,
  color: theme.palette.neutral.light,
  height: "50px",
  fontWeight: 500,
  textAlign: "center",
}));

// Helper function to truncate file name (customize as needed)
const truncate = (str) => {
  if (!str) return "";
  const parts = str.split("/");
  return parts[3] || str;
};

const SalesOrderItems = ({
  sizes,
  item_details,
  displayData,
  setData,
  data,
  activeStep,
  editStatus,
  error,
  setError,
  setDisabledSubmitBtn,
  readOnly,
  onPriceUpdate, // New prop for notifying parent of price changes
  onQuantityChange, // New prop to notify parent of quantity changes
  hideFileUpload = false, // Add this new prop
}) => {
  const [edit, setEdit] = useState(editStatus);
  const [itemDetails, setItemDetails] = useState([]);
  const [itemDetailsWithQuantity, setItemDetailsWithQuantity] = useState([]);
  const [toastState, setToastState] = useState({
    open: false,
    vertical: "top",
    horizontal: "right",
  });
  const [toastMessage, setToastMessage] = useState("");
  const handleToastClose = () => setToastState({ open: false });
  // State to store the uploaded file path (Excel file) for customization
  const [uploadFile, setUploadFile] = useState("");
  // This state determines whether to show manual entry fields or the Excel upload section.
  const [checkedCustomizeFields, setCheckedCustomizeFields] = useState(false);
  const [checkedCustomizeFieldCheck, setCheckedCustomizeFieldCheck] =
    useState(false);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  const [isLoadingShipping, setIsLoadingShipping] = useState(false);
  // Track original quantity for comparison
  const [originalQtyOnLoad, setOriginalQtyOnLoad] = useState(null);

  // Calculate total quantity from itemDetailsWithQuantity
  const calculateTotalQuantity = useCallback((details) => {
    if (!details || details.length === 0) return 0;
    return details.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0);
  }, []);

  // Fetch updated price based on new quantity
  const fetchUpdatedPrice = useCallback(
    debounce(async (priceId, newQty) => {
      if (!priceId || !newQty || newQty <= 0) return;
      
      setIsLoadingPrice(true);
      try {
        const token = getUserToken();
        const response = await fetch(
          `${apiConstants.API_URL}/price/${priceId}/qty/${newQty}`,
          {
            method: "GET",
            headers: {
              Authorization: "Bearer " + token,
              "Content-Type": "application/json",
            },
          }
        );
        const result = await response.json();
        
        if (result?.data) {
          // Price is stored as *100 in DB, keep it as is for consistency with the system
          // convertToPlaces will handle the /100 conversion for display
          const unitPrice = result.data.price; // Don't divide here - keep as *100
          const totalPrice = unitPrice * newQty;
          
          // Update data with new prices (all values stored as *100)
          let newData = { ...data };
          newData.items[activeStep].unit_price = unitPrice;
          newData.items[activeStep].total_price = totalPrice;
          newData.items[activeStep].original_total_price = totalPrice;
          newData.items[activeStep].qty = newQty;
          
          // Also update sub_total and grand_total if needed
          const estimatedShipping = newData.items[activeStep].estimated_shipping_charges || 0;
          const extraCharges = newData.items[activeStep].extra_charges || 0;
          const artworkCharges = newData.items[activeStep].artwork_charges || 0;
          
          newData.items[activeStep].sub_total = totalPrice + estimatedShipping + extraCharges + artworkCharges;
          
          setData(newData);
          
          // Notify parent component of price update
          if (onPriceUpdate) {
            onPriceUpdate(activeStep, {
              unit_price: unitPrice,
              total_price: totalPrice,
              qty: newQty,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching updated price:", error);
        setToastMessage("Failed to update price. Please try again.");
        setToastState({ open: true, vertical: "top", horizontal: "right" });
      } finally {
        setIsLoadingPrice(false);
      }
    }, 500),
    [data, activeStep, setData, onPriceUpdate]
  );

  const handleGetDelivery = useCallback((item_id, qty) => {
    const items = [{
      item_id: item_id,
      qty: qty,
    }];
    const reqData = {
      items: items,
      organization_id: data?.organization?.id,
    };

    const requestOptions = {
      method: "POST",
      headers: authHeader(),
      body: JSON.stringify(reqData),
    };
    setIsLoadingShipping(true);

    fetch(
      `${apiConstants.API_URL}/prices/shipping-charges`,
      requestOptions
    ).then((response) => {
      response.text().then((text) => {
        const result = text && JSON.parse(text);
        if (!response.ok) {
          const error = (result && result.error) || response.statusText;
          const errorMessage = Object.values(error).map((errorKey) => {
            return errorKey[0];
          });
          setToastMessage(errorMessage);
          setToastState({
            open: true,
            vertical: "top",
            horizontal: "right",
          });
        } else {
          let newData = { ...data };
          newData.items[activeStep].estimated_shipping_charges = result?.data?.total_shipping_charges;
          const estimatedShipping = newData.items[activeStep].estimated_shipping_charges || 0;
          const totalPrice = newData.items[activeStep].total_price || 0;
          const extraCharges = newData.items[activeStep].extra_charges || 0;
          const artworkCharges = newData.items[activeStep].artwork_charges || 0;
          newData.items[activeStep].sub_total = totalPrice + estimatedShipping + extraCharges + artworkCharges;          
          setData(newData);
        }        
        setIsLoadingShipping(false);
      });
    });
  }, [data, activeStep, setData, onPriceUpdate]);

  // Update local states when props change
  useEffect(() => {
    setItemDetails(item_details);
    setUploadFile(displayData?.custom_size_url);
    setEdit(false);
    // Use displayData.flag_name_no to initialize the checkbox state

    setItemDetailsWithQuantity(displayData?.itemDetailsWithQuantity || []);
    setDisabledSubmitBtn(true);

    if (
      displayData?.itemDetailsWithQuantity &&
      displayData?.itemDetailsWithQuantity.length > 0
    ) {
      if (!checkedCustomizeFieldCheck) {
        setCheckedCustomizeFields(displayData?.custom_size_url ? true : false);
      } else {
        setCheckedCustomizeFields(true);
      }
    } else {
      setCheckedCustomizeFields(false);
      setDisabledSubmitBtn(false);
    }

    if (
      item_details.every((el) => el.size_id && el.name && el.number) &&
      displayData?.flag_name_no === 1
    ) {
      setDisabledSubmitBtn(false);
    }
    let sum = 0;
    if (itemDetailsWithQuantity && itemDetailsWithQuantity.length !== 0) {
      sum = itemDetailsWithQuantity
        .map((o) => o.quantity)
        .reduce((a, c) => parseInt(a) + parseInt(c), 0);
    }
    if (
      itemDetailsWithQuantity &&
      itemDetailsWithQuantity.every((el) => el.size_id && el.quantity) &&
      parseInt(sum) === displayData?.qty
    ) {
      setDisabledSubmitBtn(false);
    }

    // Store the original quantity when component loads
    if (displayData?.qty && originalQtyOnLoad === null) {
      setOriginalQtyOnLoad(displayData.qty);
    }
  }, [
    item_details,
    displayData,
    itemDetailsWithQuantity,
    setDisabledSubmitBtn,
  ]);

  // Handler for the "Customize Name and Number" checkbox change.
  const handleCheckedChange = (e) => {
    setCheckedCustomizeFields(e.target.checked);
    setCheckedCustomizeFieldCheck(e.target.checked);
    // Optionally, you might want to clear manual entries when toggling on.
  };

  // Handlers for manual input changes remain unchanged.
  const onChangeFields = (e, index) => {
    const { id, value } = e.target;
    let newData = data;
    newData.items[activeStep].item_details[index][id] = value;
    setData(newData);
    let details = item_details;
    details[index][id] = value;
    setItemDetails(details);
    if (details.every((el) => el.size_id && el.name && el.number)) {
      setDisabledSubmitBtn(false);
    } else {
      setDisabledSubmitBtn(true);
    }
  };

  const onChangeQuantity = (e, index) => {
    const { id, value } = e.target;
    const updatedDetails = [...itemDetailsWithQuantity];
    updatedDetails[index][id] = value;
    setItemDetailsWithQuantity(updatedDetails);
    
    const sizeId = updatedDetails[index].size_id;
    const totalQty = calculateTotalQuantity(updatedDetails);
    
    // Update the data
    let newData = { ...data };
    newData.items[activeStep].itemDetailsWithQuantity = updatedDetails;
    newData.items[activeStep].qty = totalQty;
    newData.items[activeStep].qty = totalQty;

    let extraCharges = 0;
    newData.items[activeStep].sales_order_item_extra_charges?.forEach((charge) => {
      charge.qty = totalQty;
      extraCharges += charge.qty * charge.cost
    });
    newData.items[activeStep].extra_charges = extraCharges;
    
    // Track if quantity has changed from original
    const hasQtyChanged = originalQtyOnLoad !== null && totalQty !== originalQtyOnLoad;
    newData.items[activeStep].hasQtyChanged = hasQtyChanged;
    newData.items[activeStep].originalQtyOnLoad = originalQtyOnLoad;
    
    setData(newData);
    
    // Notify parent of quantity change
    if (onQuantityChange) {
      onQuantityChange(activeStep, {
        hasChanged: hasQtyChanged,
        originalQty: originalQtyOnLoad,
        newQty: totalQty,
      });
    }
    
    // Show info message instead of error, don't block
    if (totalQty !== displayData?.qty && displayData?.qty) {
      setError({
        status: true,
        message: `Total quantity: ${totalQty} (Original: ${originalQtyOnLoad || displayData?.qty})`,
        index: index,
        isWarning: true, // New flag for styling
      });
    } else {
      setError({ status: false, message: "" });
    }
    
    // Fetch updated price based on new total quantity
    const priceId = displayData?.price_id || data?.items?.[activeStep]?.price_id;
    if (priceId && totalQty > 0) {
      fetchUpdatedPrice(priceId, totalQty);
    }
    
    // Don't block the submit button
    if (sizeId) {
      setDisabledSubmitBtn(false);
    }
    const item_id = displayData?.price?.item?.id || data?.items?.[activeStep]?.price?.item?.id;
    if(item_id && totalQty > 0){
      handleGetDelivery(item_id, totalQty);
    }
  };

  const handleChange = (e, index, sizeValue) => {
    let newData = data;
    if (sizeValue) {
      newData.items[activeStep].item_details[index].size_id = sizeValue?.id;
      newData.items[activeStep].item_details[index].size_name = sizeValue?.name;
      newData.items[activeStep].item_details[index].selectedSize = {
        id: sizeValue?.id,
        name: sizeValue?.name,
      };
    }
    setData(newData);
    if (
      newData.items[activeStep].item_details.every(
        (el) => el.size_id && el.name && el.number
      )
    ) {
      setDisabledSubmitBtn(false);
    } else {
      setDisabledSubmitBtn(true);
    }
  };

  const handleChangeWithQuantity = (e, index, sizeValue) => {
    let newData = { ...data };
    if (sizeValue) {
      let updated = [...itemDetailsWithQuantity];
      updated[index].size_id = sizeValue?.id;
      updated[index].size_name = sizeValue?.name;
      updated[index].selectedSize = {
        id: sizeValue?.id,
        name: sizeValue?.name,
      };
      setItemDetailsWithQuantity(updated);
      newData.items[activeStep].itemDetailsWithQuantity[index].size_id =
        sizeValue?.id;
      newData.items[activeStep].itemDetailsWithQuantity[index].size_name =
        sizeValue?.name;
      newData.items[activeStep].itemDetailsWithQuantity[index].selectedSize = {
        id: sizeValue?.id,
        name: sizeValue?.name,
      };
      setData(newData);
    }
    
    // Don't block based on quantity matching
    if (sizeValue?.id) {
      setDisabledSubmitBtn(false);
    }
  };

  const sizesProps = {
    options: sizes,
    getOptionLabel: (option) => option?.name || "",
  };

  // --- Excel File Upload Section for Customized Name and Number ---

  const handleUploadFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("file", file);
      const token = getUserToken();
      const requestOptions = {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
        },
        body: formData,
      };
      fetch(
        `${apiConstants.API_URL}/file/sales-order-item-details`,
        requestOptions
      )
        .then((response) => response.text())
        .then((text) => {
          const res = text && JSON.parse(text);
          if (res?.data?.url) {
            setUploadFile(res.data.url);
            let newData = data;
            newData.items[activeStep].custom_size_path = res.data.path;
            setData(newData);
          }
        });
    }
  };

  const removeImage = () => {
    setUploadFile("");
  };

  // New function to add a new row for manual quantity entry (used in non-customized section)
  const addNewRow = () => {
    const newRow = {
      size_id: 0,
      quantity: "",
      selectedSize: { id: 0, name: "" },
    };
    
    if (!itemDetailsWithQuantity.every((el) => el.size_id)) {
      setToastMessage("Please select a size for all rows first");
      setToastState({
        open: true,
        vertical: "top",
        horizontal: "right",
      });
      return;
    }
    
    // Allow adding new rows without quantity restrictions
    setItemDetailsWithQuantity([...itemDetailsWithQuantity, newRow]);
    let newData = { ...data };
    newData.items[activeStep].itemDetailsWithQuantity = [
      ...itemDetailsWithQuantity,
      newRow,
    ];
    setData(newData);
  };

  const deleteRows = (index) => {
    const rows = [...itemDetailsWithQuantity];
    rows.splice(index, 1);
    setItemDetailsWithQuantity(rows);
    
    let newData = { ...data };
    newData.items[activeStep].itemDetailsWithQuantity = [...rows];
    
    // Recalculate total quantity and update price
    const totalQty = calculateTotalQuantity(rows);
    newData.items[activeStep].qty = totalQty;
    setData(newData);
    
    // Fetch updated price
    const priceId = displayData?.price_id || data?.items?.[activeStep]?.price_id;
    if (priceId && totalQty > 0) {
      fetchUpdatedPrice(priceId, totalQty);
    }

    const item_id = displayData?.price?.item?.id || data?.items?.[activeStep]?.price?.item?.id;
    if(item_id && totalQty > 0){
      handleGetDelivery(item_id, totalQty);
    }
    
    // Don't disable submit button
    setDisabledSubmitBtn(false);
  };

  const downloadUsingAnchorElement = async () => {
    const anchor = document.createElement("a");
    anchor.href = uploadFile;
    document.body.appendChild(anchor);
    anchor.click();
  };

  return (
    <>
      {itemDetailsWithQuantity && itemDetailsWithQuantity.length > 0 && (
        <>
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
                Sizes & Quantities
              </Typography>
              {(isLoadingPrice || isLoadingShipping) && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Updating details...
                  </Typography>
                </Stack>
              )}
            </Stack>
            <Typography sx={{ fontSize: "15px" }}>
              Please select the sizes you wish to add to the Order.
            </Typography>
            {/* Show current total quantity */}
            <Typography 
              sx={{ 
                fontSize: "14px", 
                color: "primary.main",
                fontWeight: 500 
              }}
            >
              Current Total Quantity: {calculateTotalQuantity(itemDetailsWithQuantity)}
            </Typography>
          </Stack>

          <Table>
            <thead>
              <StyledHeaderRow>
                <TableCell>Garment Size</TableCell>
                <TableCell>
                  {/* Using "Quantity" when not customizing */}Quantity
                </TableCell>
                <TableCell>
                  {/* "Action" column for manual entry */}Action
                </TableCell>
              </StyledHeaderRow>
            </thead>
            <tbody>
              {itemDetailsWithQuantity?.map((i, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <FormControl
                      fullWidth
                      variant="standard"
                      sx={{ width: "100%", minWidth: "200px" }}
                    >
                      <Autocomplete
                        {...sizesProps}
                        id="controlled-demo"
                        autoComplete
                        includeInputInList
                        value={i?.selectedSize}
                        onChange={(e, value) => {
                          handleChangeWithQuantity(e, index, value);
                        }}
                        disabled={!!readOnly}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Size"
                            variant="standard"
                            disabled={!!readOnly}
                          />
                        )}
                      />
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <FormControl fullWidth variant="standard">
                      <TextField
                        size="small"
                        id="quantity"
                        placeholder="Quantity"
                        value={i?.quantity}
                        onChange={(e) => onChangeQuantity(e, index)}
                        type="number"
                        InputLabelProps={{ shrink: true }}
                        disabled={!!readOnly}
                        inputProps={{ min: 0 }}
                      />
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Button 
                      onClick={() => deleteRows(index)}
                      disabled={!!readOnly}
                    >
                      <RemoveCircleOutline />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
          {!readOnly && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Button
                variant="outlined"
                sx={{ width: "200px" }}
                onClick={addNewRow}
              >
                <AddCircleOutline
                  sx={{ marginRight: "10px", width: "20px", height: "20px" }}
                />{" "}
                Add New Row
              </Button>
              {error.status && (
                <FormHelperText 
                  sx={{ 
                    color: error.isWarning ? "info.main" : "error.main",
                    fontWeight: error.isWarning ? 500 : 400,
                  }}
                >
                  {error.message}
                </FormHelperText>
              )}
            </Stack>
          )}

          {/* If NOT customizing, show manual input table */}
          {!checkedCustomizeFields ? (
            <></>
          ) : (
            // If customizing, show the Excel file upload section instead of manual fields.
            <>
              {!hideFileUpload && (
                <>
                  {!readOnly && (
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Box sx={{ width: "100%" }}>
                        <Typography sx={{ fontStyle: "italic" }}>
                          Template File:
                        </Typography>
                        <Typography sx={{ fontStyle: "italic" }}>
                          Please download the template file and fill in the details.
                        </Typography>
                      </Box>
                      <Button
                        href="/downloads/Sales Order Numbers and Names Template.xlsx"
                        variant="outlined"
                      >
                        Download Template
                      </Button>
                    </Stack>
                  )}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ mt: 1 }}
                  >
                    <Box sx={{ width: "100%" }}>
                      <Typography sx={{ fontStyle: "italic" }}>
                        Attachments:
                      </Typography>
                      <Typography sx={{ fontStyle: "italic" }}>
                        Please attach the Excel sheet of items with Numbers and
                        Names.
                      </Typography>
                    </Box>
                    {uploadFile ? (
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        sx={{
                          padding: "0 10px",
                          borderRadius: 3,
                          backgroundColor: "rgba(239, 239, 239, 0.65)",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography>{truncate(uploadFile)}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Button sx={{ minWidth: 0 }} onClick={removeImage}>
                            x
                          </Button>
                        </Box>
                      </Stack>
                    ) : (
                      <Box
                        sx={{
                          borderRadius: "8px",
                          backgroundColor: "rgba(239, 239, 239, 0.65)",
                          border: "1px dashed #9E9E9E",
                          height: "59px",
                          width: "100%",
                          padding: "0 10px",
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{ mt: 1 }}
                        >
                          <Box>
                            <Typography>File Name</Typography>
                          </Box>
                          <Box>
                            <label htmlFor="excel-upload">
                              <Button
                                variant="outlined"
                                style={{ width: "60px", pointerEvents: "none" }}
                              >
                                Upload
                              </Button>
                            </label>
                            <input
                              type="file"
                              id="excel-upload"
                              name="excel-upload"
                              accept=".xls,.xlsx"
                              onChange={handleUploadFile}
                              style={{
                                position: "absolute",
                                pointerEvents: "none",
                                visibility: "hidden",
                              }}
                            />
                          </Box>
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                  {uploadFile && (
                    <>
                      <iframe
                        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                          uploadFile
                        )}`}
                        title="Excel Preview"
                        width="100%"
                        height="100%"
                        style={{ border: "none" }}
                      />
                      <Button onClick={downloadUsingAnchorElement}>
                        Download Sizes and Names Excel File
                      </Button>
                    </>
                  )}
                </>
              )}
            </>
          )}
          <AlertToast
            toastMessage={toastMessage}
            toastState={toastState}
            handleClose={handleToastClose}
          />
        </>
      )}
    </>
  );
};

SalesOrderItems.defaultProps = {
  sizes: [],
  item_details: [],
};

SalesOrderItems.propTypes = {
  sizes: PropTypes.array,
  item_details: PropTypes.array,
};

export default SalesOrderItems;
